import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { memo, useState, useMemo, useCallback } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { Check, Close, ExpandMore } from '@mui/icons-material';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import FormMotherhood from './form';
import {
  Parenthood,
  ParenthoodSearchForm,
  SubscriptionAccordionGenerality,
  CommonInterfaceSubscription,
} from 'subscription/view-models/common-interface-subscription';

interface MotherhoodProps {
  isCohabitation?: boolean;
  isAire?: boolean;
  isTransferNew?: boolean;
  disabled?: boolean;
}

const MotherhoodComponent = ({
  isCohabitation,
  isAire,
  isTransferNew,
  disabled,
}: MotherhoodProps): React.ReactElement => {
  const [open, setOpen] = useState(false);

  const formContext = useFormikContext<CommonInterfaceSubscription>();

  const errorMotherhood = useMemo(() => {
    return !!formContext.errors.motherhood;
  }, [formContext.errors.motherhood]);

  const onChange = useCallback(
    (key, value) => {
      formContext.setFieldValue('motherhood', {
        ...(formContext.values?.motherhood || {}),
        [key]: value,
      });
    },
    [formContext.values?.motherhood, formContext.setFieldValue]
  );

  const onFieldGeneralityChange = useCallback(
    (key, value) => {
      onChange('generality', {
        ...(formContext.values?.motherhood?.generality || {}),
        [key]: value,
      });
    },
    [formContext.values?.motherhood?.generality, onChange]
  );

  const onFieldsGeneralityChange = useCallback(
    (values) => {
      onChange('generality', {
        ...(formContext.values?.motherhood?.generality || {}),
        ...values,
      });
    },
    [formContext.values?.motherhood?.generality, onChange]
  );

  const onFieldPersonChange = useCallback(
    (key, value) => {
      onChange('person', {
        ...(formContext.values?.motherhood?.person || {}),
        [key]: value,
      });
    },
    [formContext.values?.motherhood?.person, onChange]
  );

  const onFieldsPersonChange = useEventCallback((valuesNewPerson: ParenthoodSearchForm | null) => {
    if (valuesNewPerson === null) {
      onChange('person', null);
      return;
    }
    onChange('person', {
      ...(formContext.values?.motherhood?.person || {}),
      ...valuesNewPerson,
    });
  });

  const resetForm = useCallback(() => {
    formContext.setFieldValue('motherhood', null);
  }, [formContext.setFieldValue]);

  const onChangeValues = useCallback(
    (values) => {
      formContext.setFieldValue('motherhood', {
        ...formContext.values.motherhood,
        ...(values || {}),
      });
    },
    [formContext.setFieldValue, formContext.values.motherhood]
  );

  // se solo madre e entrambi faccio vedere accordion

  return formContext.values.editDataControl?.requested === '3' ||
    formContext.values.editDataControl?.requested === '1' ||
    isAire ? (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-person-motherhood-content"
        id="panel-person-motherhood-header"
        expandIcon={<ExpandMore />}
      >
        {errorMotherhood ? <Close color="error" /> : <Check color="success" />}
        <Typography>Maternità</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <FormMotherhood
          isCohabitation={isCohabitation}
          onChangePerson={onFieldPersonChange}
          onChangesPerson={onFieldsPersonChange}
          disabled={isTransferNew || disabled}
          onChangeGenerality={onFieldGeneralityChange}
          values={formContext.values.motherhood}
          resetForm={resetForm}
          onChangeValues={onChangeValues}
          onChangeValuesGenerality={onFieldsGeneralityChange}
          errorsPerson={
            (formContext.errors.motherhood as FormikErrors<Parenthood>)
              ?.person as FormikErrors<ParenthoodSearchForm>
          }
          errorsGenerality={
            (formContext.errors.motherhood as FormikErrors<Parenthood>)
              ?.generality as FormikErrors<SubscriptionAccordionGenerality>
          }
          touchedPerson={
            (formContext.touched.motherhood as unknown as FormikTouched<Parenthood>)
              ?.person as unknown as FormikTouched<ParenthoodSearchForm>
          }
          touchedGenerality={
            (formContext.touched.motherhood as unknown as FormikTouched<Parenthood>)
              ?.generality as unknown as FormikTouched<SubscriptionAccordionGenerality>
          }
        />
      </AccordionDetails>
    </Accordion>
  ) : (
    <></>
  );
};

MotherhoodComponent.displayName = 'Motherhood component';

const Motherhood = memo(MotherhoodComponent);

export default Motherhood;
